import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { QuoteRequestCard } from "./QuoteRequestCard";
import { PackageOpen, FileText, Eye, Download, Copy, Check } from "lucide-react";
import { useState, useRef } from "react";
import { SupplierQuoteRequestDocument } from "./SupplierQuoteRequestDocument";
import { useReactToPrint } from "react-to-print";
import { toast } from "sonner";
import { PRODUCTION_DOMAIN } from "@/lib/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SupplierQuotesTabProps {
  leadId: string;
}

export function SupplierQuotesTab({ leadId }: SupplierQuotesTabProps) {
  const queryClient = useQueryClient();
  const [previewRequest, setPreviewRequest] = useState<any>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const { data: quoteRequests, isLoading } = useQuery({
    queryKey: ["supplier-quote-requests", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_quote_requests")
        .select(`
          *,
          suppliers(name, email, phone, supplier_categories(name)),
          itinerary_items(title, item_type),
          profiles(full_name, email),
          leads(*)
        `)
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch brand data for all requests
  const { data: brandData } = useQuery({
    queryKey: ["brand", quoteRequests?.[0]?.leads?.brand_id],
    queryFn: async () => {
      if (!quoteRequests?.[0]?.leads?.brand_id) return null;
      const { data } = await supabase
        .from("business_brands")
        .select("logo_url")
        .eq("id", quoteRequests[0].leads.brand_id)
        .single();
      return data;
    },
    enabled: !!quoteRequests?.[0]?.leads?.brand_id,
  });

  const handleCopyLink = async (shareToken: string) => {
    const shareableUrl = `${PRODUCTION_DOMAIN}/supplier-quote-request/${shareToken}`;
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(shareToken);
      toast.success("Shareable link copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleDownloadPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Supplier-Quote-Request-${previewRequest?.id.slice(0, 8)}`,
  });

  const handleViewRequest = (request: any) => {
    window.open(`/supplier-quote-request/${request.share_token}`, '_blank');
  };

  const fullItineraryRequests = quoteRequests?.filter(
    (req) => req.request_type === "full_itinerary"
  );

  const singleItemRequests = quoteRequests?.filter(
    (req) => req.request_type === "single_item"
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Loading quote requests...</p>
      </div>
    );
  }

  if (!quoteRequests || quoteRequests.length === 0) {
    return (
      <Card className="p-8 text-center">
        <PackageOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Quote Requests Yet</h3>
        <p className="text-sm text-muted-foreground">
          Start by requesting quotes from suppliers for individual items or the full itinerary.
        </p>
      </Card>
    );
  }

  return (
    <>
      {/* Hidden print area */}
      <div className="hidden">
        {previewRequest && (
          <div ref={printRef}>
            <SupplierQuoteRequestDocument 
              quoteRequest={previewRequest}
              brandLogo={brandData?.logo_url}
            />
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Full Itinerary Requests */}
        {fullItineraryRequests && fullItineraryRequests.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Full Itinerary Quote Requests</h3>
              <Badge variant="secondary">{fullItineraryRequests.length}</Badge>
            </div>
            <div className="grid gap-4">
              {fullItineraryRequests.map((request) => (
                <div key={request.id} className="relative">
                  <QuoteRequestCard request={request} leadId={leadId} />
                  
                  {/* Action buttons */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewRequest(request)}
                      className="gap-2"
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline">
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setPreviewRequest(request);
                            setTimeout(() => handleDownloadPDF(), 100);
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleCopyLink(request.share_token)}
                        >
                          {copied === request.share_token ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Link Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Link
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {fullItineraryRequests && fullItineraryRequests.length > 0 &&
          singleItemRequests && singleItemRequests.length > 0 && (
            <Separator />
          )}

        {/* Individual Item Requests */}
        {singleItemRequests && singleItemRequests.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <PackageOpen className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Item-Specific Quote Requests</h3>
              <Badge variant="secondary">{singleItemRequests.length}</Badge>
            </div>

            {/* Group by itinerary item */}
            {(() => {
              const groupedByItem = singleItemRequests.reduce((acc, request) => {
                const itemId = request.itinerary_item_id || "unknown";
                if (!acc[itemId]) {
                  acc[itemId] = [];
                }
                acc[itemId].push(request);
                return acc;
              }, {} as Record<string, typeof singleItemRequests>);

              return (
                <div className="space-y-6">
                  {Object.entries(groupedByItem).map(([itemId, requests]) => {
                    const itemTitle = requests[0]?.itinerary_items?.title || "Unknown Item";
                    const itemType = requests[0]?.itinerary_items?.item_type || "";

                    return (
                      <div key={itemId} className="space-y-3">
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{itemTitle}</p>
                            {itemType && (
                              <p className="text-xs text-muted-foreground capitalize">{itemType}</p>
                            )}
                          </div>
                          <Badge variant="outline">{requests.length} quote{requests.length > 1 ? 's' : ''}</Badge>
                        </div>
                        <div className="grid gap-3 pl-4">
                          {requests.map((request) => (
                            <div key={request.id} className="relative">
                              <QuoteRequestCard request={request} leadId={leadId} />
                              
                              {/* Action buttons */}
                              <div className="absolute top-4 right-4 flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewRequest(request)}
                                  className="gap-2"
                                >
                                  <Eye className="h-3 w-3" />
                                  View
                                </Button>
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="outline">
                                      Actions
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setPreviewRequest(request);
                                        setTimeout(() => handleDownloadPDF(), 100);
                                      }}
                                    >
                                      <Download className="h-4 w-4 mr-2" />
                                      Download PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleCopyLink(request.share_token)}
                                    >
                                      {copied === request.share_token ? (
                                        <>
                                          <Check className="h-4 w-4 mr-2" />
                                          Link Copied!
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="h-4 w-4 mr-2" />
                                          Copy Link
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}
